/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  groupByWeeks,
  timeseriesToQuarterly,
} from "../../services/arrayService";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useDropStats } from "../../store/useDropStats";
import { useMarketPricing } from "../../store/useMarketPricing";
import DropdownFilter from "../modals/DropdownFilter";

// Extend dayjs with weekOfYear plugin
dayjs.extend(weekOfYear);

interface Props {
  periodProp?: "Daily" | "Weekly" | "LastTwelweMonths" | "Monthly" | "Yearly";
  weeklyRangeProp?: Date;
  monthRangeProp?: Date;
  yearRangeProp?: Date;
  selectedFiltersProp?: string[];
}

export const AverageDropSize = React.memo(
  ({
    periodProp,
    weeklyRangeProp,
    monthRangeProp,
    yearRangeProp,
    selectedFiltersProp,
  }: Props) => {
    /** Refs */
    const snapshotRef = useRef(null);
    /** Hooks */
    const { averageDropSizes, getAverageDropSize } = useDropStats();
    const { marketPrices, getAllMarketPrices } = useMarketPricing();
    const [_, takeScreenshot] = useScreenshot();
    /** State */
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [period, setPeriod] = useState<
      "Weekly" | "Monthly" | "LastTwelweMonths" | "Quarterly" | "Yearly"
    >("LastTwelweMonths");
    const [monthRange, setMonthRange] = useState(new Date());
    const [yearRange, setYearRange] = useState(new Date());

    /** Callbacks */
    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        // Create a link to download the image
        const link = document.createElement("a");
        link.href = image;
        link.download = "amount_of_drops.png"; // Set the default file name
        link.click(); // Programmatically click the link to trigger download
      });
    };

    const filterOptions = useMemo(() => {
      const kgOptions = marketPrices?.filter((mp) => mp?.measurement === "kg");
      return [
        "All",
        ...(kgOptions?.length ? kgOptions?.map((price) => price.name) : []),
        "Total",
      ];
    }, [marketPrices]);

    const transformedData = useMemo(() => {
      if (!averageDropSizes) return [];

      // Filter series based on selected filters
      const filteredSeries = averageDropSizes.filter((series) =>
        selectedFilters.includes(series.name?.replaceAll(" Active Users", ""))
      );

      return filteredSeries;
    }, [averageDropSizes, selectedFilters]);

    const onSelectFilters = useCallback(
      (option: string) => {
        if (option === "All") {
          if (selectedFilters.includes("All")) {
            setSelectedFilters([]);
            return;
          }
          setSelectedFilters([
            "All",
            ...(marketPrices?.length
              ? marketPrices?.map((price) => price.name)
              : []),
            "Total",
          ]);
          return;
        }
        setSelectedFilters((prev: any) =>
          prev.includes(option)
            ? prev
                .filter((item: any) => item !== option)
                ?.filter((item: any) => item !== "All")
            : [...prev, option]?.filter((item: any) => item !== "All")
        );
      },
      [marketPrices, selectedFilters, filterOptions]
    );

    const getLastMonth = () =>
      setMonthRange(dayjs(monthRange).subtract(1, "month").toDate());
    const getNextMonth = () =>
      setMonthRange(dayjs(monthRange).add(1, "month").toDate());

    const getLastYear = () =>
      setYearRange(dayjs(yearRange).subtract(1, "year").toDate());
    const getNextYear = () =>
      setYearRange(dayjs(yearRange).add(1, "year").toDate());

    const getMonths = (date: Date) => {
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      const currentMonth = date.getMonth(); // getMonth() returns 0 for January, 11 for December

      return monthNames.slice(0, currentMonth + 1);
    };

    const monthlyTickAmount = useMemo(() => {
      const months = getMonths(yearRange);
      if (new Date().getFullYear() === yearRange.getFullYear()) {
        return months.length + 1;
      }
      return 11;
    }, [yearRange]);

    /** Effects */
    useEffect(() => {
      if (periodProp && periodProp !== "Daily") setPeriod(periodProp);
      if (weeklyRangeProp) setMonthRange(weeklyRangeProp);
      if (monthRangeProp) setMonthRange(monthRangeProp);
      if (yearRangeProp) setYearRange(yearRangeProp);
      if (selectedFiltersProp) setSelectedFilters(selectedFiltersProp);
    }, [
      periodProp,
      weeklyRangeProp,
      monthRangeProp,
      yearRangeProp,
      selectedFiltersProp,
    ]);

    useEffect(() => {
      getAllMarketPrices();
    }, []);

    useEffect(() => {
      setSelectedFilters([
        "All",
        ...(marketPrices?.length
          ? marketPrices?.map((price) => price.name)
          : []),
        "Total",
      ]);
    }, [marketPrices]);

    useEffect(() => {
      if (period === "Weekly")
        getAverageDropSize(
          monthRange,
          period?.toLowerCase() as "weekly" | "monthly" | "yearly"
        );
      if (period === "Monthly" || period === "Quarterly")
        getAverageDropSize(
          yearRange,
          "monthly" as "weekly" | "monthly" | "yearly"
        );
      if (period === "LastTwelweMonths") {
        getAverageDropSize(new Date(), "lastTwelweMonths");
      }
      if (period === "Yearly") {
        getAverageDropSize(
          new Date(),
          period?.toLowerCase() as "weekly" | "monthly" | "yearly"
        );
      }
    }, [period, monthRange, yearRange]);

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 border border-gray-border drop-shadow-md mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-2 sm:mb-12">
            {/* Volumes of the week */}
            Average drop size
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
            {period === "Weekly" ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
                <PrimaryButton size="sm" onClick={getLastMonth} label="Prev" />
                <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                  {dayjs(monthRange).format("MMMM YYYY")}
                </div>
                <PrimaryButton
                  size="sm"
                  disabled={dayjs(monthRange).add(1, "month").isAfter(dayjs())}
                  onClick={getNextMonth}
                  label="Next"
                />
              </div>
            ) : null}
            {period === "Monthly" || period === "Quarterly" ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
                <PrimaryButton size="sm" onClick={getLastYear} label="Prev" />
                <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                  {dayjs(yearRange).format("YYYY")}
                </div>
                <PrimaryButton
                  size="sm"
                  disabled={dayjs(yearRange).add(1, "year").isAfter(dayjs())}
                  onClick={getNextYear}
                  label="Next"
                />
              </div>
            ) : null}
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {period === "LastTwelweMonths" ? "Last 12 months" : period}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={period === "Weekly"}
                  onClick={() => setPeriod("Weekly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Weekly
                </MenuItem>
                <MenuItem
                  disabled={period === "Monthly"}
                  onClick={() => setPeriod("Monthly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Monthly
                </MenuItem>
                <MenuItem
                  disabled={period === "LastTwelweMonths"}
                  onClick={() => setPeriod("LastTwelweMonths")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Last 12 months
                </MenuItem>
                <MenuItem
                  disabled={period === "Quarterly"}
                  onClick={() => setPeriod("Quarterly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Quarterly
                </MenuItem>
                <MenuItem
                  disabled={period === "Yearly"}
                  onClick={() => setPeriod("Yearly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Yearly
                </MenuItem>
              </MenuList>
            </Menu>
            <div className="ml-0 sm:ml-2" />
            <DropdownFilter
              label="Filters"
              options={filterOptions}
              selectedOptions={selectedFilters}
              onSelect={onSelectFilters}
            />
            <div className="ml-0 sm:ml-2" />
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
            />
          </div>
        </div>
        <div ref={snapshotRef}>
          {transformedData && period === "Weekly" ? (
            <Chart
              // @ts-ignore
              options={{
                ...options,
                tooltip: {
                  theme: "dark",
                },
                xaxis: {
                  tickAmount:
                    groupByWeeks(transformedData)?.[0]?.data?.length - 1,
                  labels: {
                    // @ts-ignore
                    formatter: function (_, timestamp: number) {
                      // Calculate the week number for the current timestamp

                      return `Week ${timestamp}`;
                    },
                  },
                },
              }}
              series={groupByWeeks(transformedData)}
              // type="line"
              height={300}
            />
          ) : null}
          {transformedData && period === "Monthly" ? (
            <Chart
              options={{
                ...options,
                xaxis: {
                  // Only show 1 tick when there's only one month
                  tickAmount: monthlyTickAmount,
                  labels: {
                    // @ts-ignore
                    formatter: function (value: number) {
                      const thisMonth = dayjs(value)
                        .add(5, "day")
                        .format("MMM");
                      const currentMonth = dayjs(new Date()).format("MMM");
                      const isSameYear =
                        yearRange.getFullYear() === new Date().getFullYear();
                      if (currentMonth === thisMonth && isSameYear) {
                        return "Past 30 days";
                      }
                      return dayjs(value).add(5, "day").format("MMM");
                    },
                  },
                },
              }}
              series={transformedData}
              height={300}
            />
          ) : null}
          {transformedData && period === "LastTwelweMonths" ? (
            <Chart
              options={{
                ...options,
                xaxis: {
                  // Only show 1 tick when there's only one month
                  tickAmount: 11,
                  labels: {
                    // @ts-ignore
                    formatter: function (value: number) {
                      return dayjs(value).add(5, "day").format("MMM");
                    },
                  },
                },
              }}
              series={transformedData}
              height={300}
            />
          ) : null}
          {transformedData && period === "Quarterly" ? (
            <Chart
              options={{
                ...options,
                xaxis: {
                  // Only show 1 tick when there's only one month
                  tickAmount: 3,
                  labels: {
                    // @ts-ignore
                    formatter: function (value: number) {
                      return `Q${value}`;
                    },
                  },
                },
              }}
              series={timeseriesToQuarterly((transformedData as any) ?? [])}
              height={300}
            />
          ) : null}
          {transformedData && period === "Yearly" ? (
            <Chart
              // @ts-ignore
              options={{
                ...options,
                xaxis: {
                  tickAmount: 3,
                  labels: {
                    // @ts-ignore
                    formatter: function (value: number) {
                      return dayjs(value).format("YYYY");
                    },
                  },
                },
              }}
              series={transformedData}
              // type="line"
              height={300}
            />
          ) : null}
        </div>
      </div>
    );
  }
);

const options = {
  chart: {
    type: "line",
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  grid: {
    strokeDashArray: 5,
  },
  markers: {
    size: 1,
  },
  // Add yaxis configuration
  yaxis: {
    title: {
      text: "Average weight (kg)",
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },
    labels: {
      formatter: (value: number) => {
        return `${value.toFixed(2)} kg`; // or value.toFixed(0) for no decimals
      },
    },
  },
  // Add tooltip configuration to format the values in tooltips as well
  tooltip: {
    y: {
      formatter: (value: number) => {
        return `${value.toFixed(2)} kg`; // or value.toFixed(0) for no decimals
      },
    },
  },
  colors: [
    "#23C16B", // Green
    "#FFB323", // Yellow
    "#48A7F8", // Blue
    "#FF3333", // Red
    "#FF8800", // Orange
    "#9259FF", // Purple
    "#FF4DA6", // Pink
    "#33FFE6", // Teal
    "#808080", // Gray
    "#D17A50", // Brown
    "#FF7F50", // Coral
    "#8A2BE2", // BlueViolet
    "#5F9EA0", // CadetBlue
    "#DC143C", // Crimson
    "#00FFFF", // Cyan
    "#FF1493", // DeepPink
    "#696969", // DimGray
    "#B8860B", // DarkGoldenrod
    "#FF4500", // OrangeRed
    "#32CD32", // LimeGreen
    "#4682B4", // SteelBlue
    "#00FA9A", // MediumSpringGreen
    "#FFD700", // Gold
    "#FF6347", // Tomato
    "#7B68EE", // MediumSlateBlue
    "#008080", // Teal
    "#6A5ACD", // SlateBlue
    "#D2691E", // Chocolate
    "#B22222", // FireBrick
    "#9ACD32", // YellowGreen
    "#8B008B", // DarkMagenta
    "#00CED1", // DarkTurquoise
    "#FF69B4", // HotPink
    "#1E90FF", // DodgerBlue
    "#40E0D0", // Turquoise
    "#EE82EE", // Violet
  ],
};
